import LoadingButton from "@mui/lab/LoadingButton";
import { Alert, Checkbox, FormControlLabel, Grid, Snackbar, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Form, Formik } from "formik";
import _ from "lodash";
import React, { memo, useState } from "react";
import * as Yup from "yup";

import getClient from "../../core/http-common";
import { useStatesQuery } from "../../core/queries";
import CaptchaCredits from "../CaptchaCredits";
import Dropdown from "../Dropdown";
import TextField from "../FormComponents/TextField";

const StateDropdown = styled(Dropdown)`
	flex-basis: 60%;
`;

export const AppraisalsSchema = Yup.object().shape({
	name: Yup.string()
		.required("Please provide your name.")
		.max(127, "Please enter a maximum of 127 characters"),
	street: Yup.string()
		.required("Please provide your street.")
		.max(127, "Please enter a maximum of 127 characters"),
	city: Yup.string()
		.required("Please provide your city.")
		.max(127, "Please enter a maximum of 127 characters"),
	state: Yup.string().required("Please provide your state."),
	zip: Yup.string()
		.required("Please provide your postal code.")
		.max(127, "Please enter a maximum of 127 characters"),
	email: Yup.string()
		.email("Invalid email")
		.required("Please provide your email address")
		.max(127, "Please enter a maximum of 127 characters"),
	phoneNumber: Yup.string()
		.required("Please provide your phone number.")
		.max(127, "Please enter a maximum of 127 characters"),
	companyName: Yup.string()
		.notRequired()
		.max(127, "Please enter a maximum of 127 characters"),
	additionalInformation: Yup.string()
		.notRequired()
		.max(1023, "Please enter a maximum of 1023 characters"),
});

const statesOptions = (statesData, stateName, setFieldValue) => {
	if (
		statesData &&
		stateName &&
		!statesData.find((state) => state.name == stateName)
	) {
		// setFieldValue('stateId', '')
		setFieldValue("state", statesData[0].name); // Remove stateId and uncomment to select first country in dropdown on change
	}

	return statesData?.map((state) => {
		return { label: state.name, value: state.name };
	});
};

const FormChild = ({
	values,
	setFieldValue,
	handleSubmit,
	isSubmitting,
	isValid,
	validateForm,
	touched,
	...props
}) => {
	const { isLoading: isStatesDataLoading, data: statesData } = useStatesQuery(
		values.country
	);

	const handleClick = async (validateForm) => {
		const result = await validateForm();
		window.posthog?.capture("appraisals page submit button", {
			formIsValid: _.isEmpty(result),
		});
	};

	return (
		<>
			<Form onSubmit={handleSubmit} id="appraisals-form">
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<TextField
							id={"appraisals_name"}
							name={"name"}
							label={"Name"}
							data-testid={"appraisals_name"}
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							id={"appraisals_street"}
							name={"street"}
							label={"Address"}
							data-testid={"appraisals_address"}
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							id={"appraisals_city"}
							name={"city"}
							label={"City"}
						/>
					</Grid>
					<Grid item xs={8}>
						<StateDropdown
							id={"appraisals_state"}
							name={"state"}
							label={"State"}
							options={
								!isStatesDataLoading
									? statesOptions(
											statesData,
											values.state,
											setFieldValue
									  )
									: null
							}
							disabled={isStatesDataLoading}
						/>
					</Grid>
					<Grid item xs={4}>
						<TextField
							id={"appraisals_zip"}
							name={"zip"}
							label={"Postal Code"}
						/>
					</Grid>
					<Grid item xs={12}>
						<Dropdown
							id={"appraisals_country"}
							name={"country"}
							label={"Country"}
							options={[
								{ value: "236", label: "United States" },
								{ value: "40", label: "Canada" },
								{ value: "144", label: "Mexico" },
							]}
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							id={"appraisals_email"}
							name={"email"}
							label={"Email"}
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							id={"appraisals_phone"}
							name={"phoneNumber"}
							label={"Phone Number"}
						/>
						<FormControlLabel
								control={<Checkbox sx={{color: '#595959', alignSelf: 'self-start', pt: '0'}} checked={values.smsAgreement}/>}
								name="smsAgreement"
								label={
									<Typography sx={{fontSize: '10px', color: '#595959'}}>
										<span style={{fontWeight: 500}}>(Optional)&nbsp;</span>I consent to receive SMS messages to the phone number provided for notifications and alerts from Purple Wave, Inc. I understand that I am not required to provide my consent as a condition of purchasing any products or services. I understand I can opt out at any time by responding STOP. I can reply with HELP to get help. Message volumes vary. Message and data rates may apply.&nbsp;
										<a style={{color: "#2A61A3"}} href="/auction/legal/privacy">Privacy Policy</a>
									</Typography>
								}
								onChange={e => {
									setFieldValue('smsAgreement', e.target.checked)
								}}
								sx={{mt: 2}}
							/>
					</Grid>
					<Grid item xs={12} md={12}>
						<TextField
							id={"appraisals_company"}
							name={"companyName"}
							label={"Company Name [optional]"}
						/>
					</Grid>
					<Grid item xs={12} md={12}>
						<TextField
							id={"appraisals_additional"}
							name={"additionalInformation"}
							label={"Additional Information [optional]"}
							multiline={true}
							minRows={4}
						/>
					</Grid>
					<Grid item xs={12}>
						<CaptchaCredits />
					</Grid>
					<Grid container item xs={12} justifyContent="flex-start">
						<LoadingButton
							onClick={() => handleClick(validateForm)}
							loading={isSubmitting}
							type="submit"
						>
							Submit
						</LoadingButton>
					</Grid>
				</Grid>
			</Form>
		</>
	);
};

function handleError(setDisplaySnackbar) {
	setDisplaySnackbar(true);
}

function handleSuccess(setDisplaySnackbar) {
	setDisplaySnackbar(true);
	window.posthog?.capture("appraisals form submission");
}

function handleErrorClose(setDisplaySnackbar) {
	setDisplaySnackbar(false);
}

function handleSuccessClose(setDisplaySnackbar) {
	setDisplaySnackbar(false);
}

function AppraisalsForm({ setTouchedTrue }) {
	const [displayErrorSnackbar, setDisplayErrorSnackbar] = useState(false);
	const [displaySuccessSnackbar, setDisplaySuccessSnackbar] = useState(false);

	const FormBorder = styled('div')`
		background-color: ${(props) =>
			props.theme.palette.secondary.extra_light};
		padding: 16px;
		border-radius: 4px;
	`;

	return (
		<>
			<FormBorder>
				<Formik
					initialValues={{
						name: "",
						street: "",
						city: "",
						zip: "",
						country: "236",
						email: "",
						phoneNumber: "",
						smsAgreement: false,
						companyName: "",
						additionalInformation: "",
					}}
					validationSchema={AppraisalsSchema}
					onSubmit={async (values, { setSubmitting }) => {
						window.grecaptcha.enterprise.ready(async () => {
							try {
								const captchaRes =
									await window.grecaptcha.enterprise.execute(
										process.env.REACT_APP_CAPTCHA_KEY,
										{
											action: "APPRAISAL_REQUEST",
										}
									);
								await getClient().post(
									"/v1/appraisals/submit",
									{
										form_data: values,
										type: "appraisal",
										"g-recaptcha-response": captchaRes,
									}
								);
								window.posthog?.capture(`SMS checkbox ${values.smsAgreement ? 'Checked' : 'Unchecked'} in appraisal form`,{
									phoneNumber: values.phoneNumber,
								})
								handleSuccess(setDisplaySuccessSnackbar);
							} catch (error) {
								handleError(setDisplayErrorSnackbar);
							}
							setSubmitting(false);
						});
					}}
				>
					{(props) => (
						<FormChild {...props} setTouchedTrue={setTouchedTrue} />
					)}
				</Formik>
			</FormBorder>
			<Snackbar
				open={displayErrorSnackbar}
				onClose={() => handleErrorClose(setDisplayErrorSnackbar)}
			>
				<Alert
					onClose={() => handleErrorClose(setDisplayErrorSnackbar)}
					severity="error"
					variant="filled"
				>
					Data not submitted, please try again
				</Alert>
			</Snackbar>
			<Snackbar
				open={displaySuccessSnackbar}
				onClose={() => handleSuccessClose(setDisplaySuccessSnackbar)}
			>
				<Alert
					onClose={() =>
						handleSuccessClose(setDisplaySuccessSnackbar)
					}
					severity="success"
					variant="filled"
				>
					Form Submitted Successfully
				</Alert>
			</Snackbar>
		</>
	);
}

export default memo(AppraisalsForm);
